import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

// Dynamic row measurement for react-virtual. An entry row is a Fragment of
// a main `<TableRow data-index=i>` plus, when expanded, a sibling
// breakdown `<TableRow data-expansion-index=i>`. react-virtual measures one
// element per item, so the item's true height is the main row's height PLUS
// the expansion sibling's when it's mounted — otherwise the expanded panel
// isn't counted and the bottom becomes unreachable while a row is open.
const measureRowWithExpansion = (el: Element): number => {
  const main = el as HTMLElement;
  let height = main.offsetHeight;
  const sibling = main.nextElementSibling as HTMLElement | null;
  if (
    sibling &&
    sibling.getAttribute('data-expansion-index') === main.getAttribute('data-index')
  ) {
    height += sibling.offsetHeight;
  }
  return height;
};
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import {
  EntryRule,
  UpdateEntryRuleDto,
  ManualConfirmedPair,
  ENTRY_HARD_CAP_GROSS,
  EnrichedEntryStatus,
  EntrySimulationResult,
  MakerFlag
} from '../../types/entry.types';
import {
  useSetPairOpenerEnabled,
  useBulkSetPairOpenerEnabled
} from '../../api/useEntry';
import { Comparator, LiveStore } from '../../live/createLiveStore';
import { useLiveOrder, useLiveState } from '../../live/useLiveStore';
import { usePositionsGrouped } from '../../api/usePositions';
import { BulkActionBar } from '../BulkActionBar';
import {
  KALSHI_TAKER_FEE_RATE,
  polymarketTakerFeeRate,
  takerTnaFromSum,
  fmtPrice,
  fmtQty,
  fmtPct,
  TAB_FLAGS
} from './entryMath';
import { HedgeCheckEditorDialog } from './HedgeCheckEditorDialog';

interface PairContracts {
  polyYes: number;
  polyNo: number;
  kalshiYes: number;
  kalshiNo: number;
}

const ZERO_CONTRACTS: PairContracts = {
  polyYes: 0,
  polyNo: 0,
  kalshiYes: 0,
  kalshiNo: 0
};

const fmtContracts = (v: number): string => {
  if (!v) return '—';
  return Math.round(v).toString();
};

// Engine session-contracts formatter. `0` is a real value here (the engine
// holds the thread but bought nothing yet this session), so it renders "0";
// only absent (no live envelope / inactive path) shows "—".
const fmtSession = (v: number | undefined | null): string =>
  v == null ? '—' : Math.round(v).toString();

// The engine's per-thread session-bought for one arb path, off the live
// believed-position envelope. `poly`/`kalshi` are null when the engine holds
// no slot for that path (e.g. a single-direction rule's inactive path, or
// before the first live envelope).
const sessionForPath = (
  believed: EnrichedEntryStatus['believedPosition'] | undefined,
  path: 'a' | 'b'
): { poly: number | null; kalshi: number | null } => {
  const p = believed?.paths.find(x => x.path === path);
  return {
    poly: p?.polymarket.sessionContractsBought ?? null,
    kalshi: p?.kalshi.sessionContractsBought ?? null
  };
};

/**
 * Per-input save affordance shared by Threshold / Size / MaxV cells.
 * Renders nothing when the field is clean and not mid-save so the cell
 * stays compact at rest. Each input gets its own copy so the operator
 * sees the tick next to the exact field they edited.
 */
function renderSaveIndicator(
  saving: boolean,
  dirty: boolean,
  onClick: () => void
): JSX.Element | null {
  if (saving && dirty) return <CircularProgress size={12} sx={{ ml: 0.2 }} />;
  if (saving) return null;
  if (!dirty) return null;
  return (
    <IconButton
      size="small"
      onClick={onClick}
      aria-label="save"
      sx={{ p: 0.1, ml: 0.2, color: 'success.main' }}
    >
      <CheckIcon sx={{ fontSize: '0.8rem' }} />
    </IconButton>
  );
}

const hSx = {
  fontSize: '0.58rem',
  fontWeight: 600,
  color: 'grey.500',
  py: 0.4,
  px: 0.1,
  lineHeight: 1.2,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.02em',
  whiteSpace: 'nowrap' as const,
  borderBottom: '1px solid rgba(255,255,255,0.06)'
};

// Sticky offset for the second header row. Must equal row 1's actual
// rendered height in pixels, otherwise the second row either covers
// part of row 1 (overlap) or floats over the body (gap → first body
// row scrolls behind it). With `py: 0.4` + fontSize 0.58rem +
// lineHeight 1.2 + 1px border the row is ~21px.
const ROW1_HEIGHT = 21;

// Second-header-row cells get extra bottom padding + a stronger
// divider so there's visible breathing room between the sub-header
// and the first body row. Bump pb if the operator reports the body
// row 1 still looks crowded against the sub-header.
const subHSx = {
  pb: 1.2,
  borderBottom: '2px solid rgba(255,255,255,0.18)'
};
const gSx = {
  ...hSx,
  textAlign: 'center' as const,
  borderBottom: '1px solid rgba(255,255,255,0.1)'
};
const cSx = { fontSize: '0.72rem', py: 0.15, px: 0.05 } as const;

export interface CreateEntrySumRuleParams {
  pair: ManualConfirmedPair;
  /**
   * Which of the 4 maker flags this row activated on. Drives the initial
   * path. Null when activation came from TT or a pre-rule paused-create
   * via the param ✓.
   */
  makerFlag: MakerFlag | null;
  /**
   * Optional initial strategy. Defaults to 'maker-first' on the backend.
   * Set to 'taker-taker' when the row activates via the TT switch with
   * no maker flag set.
   */
  entryStrategy?: 'maker-first' | 'taker-taker';
  /**
   * Path of the tab the activation happened in. Drives the rule's poly /
   * kalshi sides when `makerFlag` is null (TT-only or paused-create from
   * params). Ignored when `makerFlag` is set — sides derive from the flag
   * via FLAG_TO_SIDES.
   */
  tabPath: 'a' | 'b';
  thresholdSum: number;
  size: number;
  maxVolumeContracts: number;
}

interface Props {
  /**
   * Which entry path this tab governs. Tab A = poly YES + kalshi NO;
   * Tab B = poly NO + kalshi YES.
   */
  path: 'a' | 'b';
  pairs: ManualConfirmedPair[];
  rules: EntryRule[] | undefined;
  anyRulesByPairId: Map<string, EntryRule>;
  /** Per-pair external store for the live price/sim state. Rows subscribe
   *  per-key; the tab subscribes to the throttled sorted-id order. */
  store: LiveStore<EnrichedEntryStatus>;
  onCreateRule: (params: CreateEntrySumRuleParams) => Promise<void>;
  onSetMakerFlag: (
    rule: EntryRule,
    flag: MakerFlag,
    value: boolean,
    clearOthers?: MakerFlag[]
  ) => Promise<void> | void;
  onUpdateRule: (rule: EntryRule, dto: UpdateEntryRuleDto) => Promise<boolean>;
  onHidePair: (pair: ManualConfirmedPair) => Promise<void> | void;
  /** Opens the "Engine position" drawer for this row's rule. */
  onOpenEnginePosition: (ruleId: string, pairName: string) => void;
}

const DEFAULT_THRESHOLD = 0.95;
const DEFAULT_SIZE = 50;
const DEFAULT_MAX_VOL = 500;

// Proportional column widths fed to a <colgroup>. Same pattern as the
// unwinder's Sum-of-Sides table (see `UnwindingTabSumOfSides.tsx`):
// `tableLayout: 'fixed'` + per-cell pixel widths sums to ~1324 px and
// the browser refuses to stretch the table to fill a wider container.
// Listing the widths once as percentages of the total gives every
// column its proportional share of 100% of the table width. Order
// matches the rendered column sequence: checkbox · Market · YES · NO ·
// Polymarket (Qty/Bid/Ask/Qty) · Kalshi (Qty/Bid/Ask/Qty) · Parameters
// (Thr/Size/MaxV) · 3 strategy groups (Legs + $/% each) · Ctrl (P/K/TT).
const COL_WIDTHS_PX = [
  18, 440, 56, 56, 54, 54, 24, 32, 32, 24, 24, 32, 32, 24, 40, 30, 36, 50, 50,
  50, 50, 50, 50, 28, 28, 28
] as const;
const COL_TOTAL_PX = COL_WIDTHS_PX.reduce((s, w) => s + w, 0);

type SortKey =
  // Resting/default order: NOT tied to a column header. Deterministic
  // composite — market-data tier, then opener-enabled, then A→Z by name.
  // This is what the table shows on mount so the row order never jitters
  // on a price tick and is identical across reconnects. The operator
  // opts into a live metric sort by clicking a column header.
  | 'default'
  | 'name'
  | 'sumTT'
  | 'tnaTT'
  | 'sumPolyMk'
  | 'tnaPolyMk'
  | 'sumKalshiMk'
  | 'tnaKalshiMk';
type SortDir = 'asc' | 'desc';

// Sums sort ascending by default (cheapest combined cost first); TNAs
// sort descending by default (highest annualized return first).
const SUM_KEYS: SortKey[] = ['sumTT', 'sumPolyMk', 'sumKalshiMk'];

/* ------------------------------------------------------------------ */
/* Math helpers — per-pair                                            */
/* ------------------------------------------------------------------ */

interface PathMetrics {
  polyAsk: number | null;
  polyBid: number | null;
  kalshiAsk: number | null;
  kalshiBid: number | null;
  polyAllIn: number | null;
  kalshiAllIn: number | null;
  polyMakerRest: number | null;
  kalshiMakerRest: number | null;
  sumTT: number | null;
  sumPolyMk: number | null;
  sumKalshiMk: number | null;
  tnaTT: number | null;
  tnaPolyMk: number | null;
  tnaKalshiMk: number | null;
}

const NULL_PATH_METRICS: PathMetrics = {
  polyAsk: null,
  polyBid: null,
  kalshiAsk: null,
  kalshiBid: null,
  polyAllIn: null,
  kalshiAllIn: null,
  polyMakerRest: null,
  kalshiMakerRest: null,
  sumTT: null,
  sumPolyMk: null,
  sumKalshiMk: null,
  tnaTT: null,
  tnaPolyMk: null,
  tnaKalshiMk: null
};

const computePathMetrics = (
  path: 'a' | 'b',
  liveState: EnrichedEntryStatus | undefined
): PathMetrics => {
  // When the per-pair opener kill switch is off (`pairEnabled !== true`)
  // the opener is bringing NO data for this pair. Every column —
  // raw market quotes AND the engineSim-derived sums — must collapse to
  // "—": rendering the last (now stale) numbers would lie. Mirrors the
  // unwinder's `isMonitored` gate in UnwindingTabSumOfSides.
  if (liveState?.pairEnabled !== true) {
    return NULL_PATH_METRICS;
  }
  // Raw top-of-book bid/ask for the tab's path (rendered in the
  // platform Bid/Ask cells). Backend WS quotes from the MarketState.
  const polyAsk =
    (path === 'a' ? liveState?.polyYesAsk : liveState?.polyNoAsk)?.price ??
    null;
  const polyBid =
    (path === 'a' ? liveState?.polyYesBid : liveState?.polyNoBid)?.price ??
    null;
  const kalshiAsk =
    (path === 'a' ? liveState?.kalshiNoAsk : liveState?.kalshiYesAsk)?.price ??
    null;
  const kalshiBid =
    (path === 'a' ? liveState?.kalshiNoBid : liveState?.kalshiYesBid)?.price ??
    null;

  // Strategy sums, maker rest, and all-in taker prices ALL come from the
  // backend `engineSim` payload — the SAME helpers the engine triggers
  // orders on (depth-walked VWAPs + findBestMakerPrice). There is NO
  // client-side fallback: the FE must never approximate these numbers.
  // When `engineSim` is absent (backend has no orderbook for the pair)
  // every sim field is null and the cell renders "—".
  const engine = liveState?.engineSim?.[path];
  const polyAllIn = engine?.polyTakerAllInVwap ?? null;
  const kalshiAllIn = engine?.kalshiTakerAllInVwap ?? null;
  const polyMakerRest = engine?.polyMakerRest ?? null;
  const kalshiMakerRest = engine?.kalshiMakerRest ?? null;
  const sumTT = engine?.sumTT ?? null;
  const sumPolyMk = engine?.sumPolyMk ?? null;
  const sumKalshiMk = engine?.sumKalshiMk ?? null;

  const days = liveState?.daysToExpiry ?? null;
  return {
    polyAsk,
    polyBid,
    kalshiAsk,
    kalshiBid,
    polyAllIn,
    kalshiAllIn,
    polyMakerRest,
    kalshiMakerRest,
    sumTT,
    sumPolyMk,
    sumKalshiMk,
    tnaTT: takerTnaFromSum(sumTT, days),
    tnaPolyMk: takerTnaFromSum(sumPolyMk, days),
    tnaKalshiMk: takerTnaFromSum(sumKalshiMk, days)
  };
};

// The live sort metric for one pair under a given sort key. Mirrors the
// per-key switch the old `sortedPairs` annotation used. `null` for the
// resting/name sorts (those order by name, not a metric).
const metricForSort = (
  metrics: PathMetrics,
  sortKey: SortKey
): number | null => {
  switch (sortKey) {
    case 'sumTT':
      return metrics.sumTT;
    case 'tnaTT':
      return metrics.tnaTT;
    case 'sumPolyMk':
      return metrics.sumPolyMk;
    case 'tnaPolyMk':
      return metrics.tnaPolyMk;
    case 'sumKalshiMk':
      return metrics.sumKalshiMk;
    case 'tnaKalshiMk':
      return metrics.tnaKalshiMk;
    default:
      return null;
  }
};

/* ------------------------------------------------------------------ */
/* Table                                                              */
/* ------------------------------------------------------------------ */

export const EntryTabByPath = ({
  path,
  pairs,
  rules,
  anyRulesByPairId,
  store,
  onCreateRule,
  onSetMakerFlag,
  onUpdateRule,
  onHidePair,
  onOpenEnginePosition
}: Props) => {
  // Resting sort is the deterministic 'default' composite (market-data →
  // opener-enabled → A→Z), so the table opens in a stable order and stays
  // there across price ticks and WS reconnects. Clicking a column header
  // switches to that column's live metric sort.
  const [sortKey, setSortKey] = useState<SortKey>('default');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Per-rule TNA simulation breakdown — at most one row expanded at a
  // time (mirrors the unwinder's TNA tab). Keyed by pair id (else name).
  const [expandedPairId, setExpandedPairId] = useState<string | null>(null);
  const handleToggleExpand = useCallback((pairKey: string) => {
    setExpandedPairId(prev => (prev === pairKey ? null : pairKey));
  }, []);

  // Freeze-sort: when on, the live store holds the current sorted order so
  // price ticks stop reshuffling the table (cells keep ticking). The store
  // owns the held order internally via `setOrderInputs({ freeze })`; new
  // pairs that appear while frozen tail-sort by the comparator off the
  // store's distinct-id set.
  const [frozen, setFrozen] = useState(false);

  const rulesByPairId = useMemo(() => {
    const map = new Map<string, EntryRule>();
    for (const rule of rules ?? []) {
      if (!rule.pairId) continue;
      map.set(rule.pairId, rule);
    }
    return map;
  }, [rules]);

  // Current open contracts split by (platform × side) per pair.
  // Mirrors the unwinder (UnwindingPage.tsx) which uses the same REST
  // hook; the /opening route is NOT wrapped by PositionsProvider, so
  // the context-based usePositions() throws here. 10s poll matches the
  // unwinder's freshness budget.
  const { data: positionsData } = usePositionsGrouped({ status: 'open' });
  const contractsByPairId = useMemo(() => {
    const map = new Map<string, PairContracts>();
    const groups = positionsData?.groups ?? [];
    for (const g of groups) {
      if (!g._id) continue;
      let polyYes = 0;
      let polyNo = 0;
      let kalshiYes = 0;
      let kalshiNo = 0;
      for (const p of g.positions) {
        if (p.status !== 'open' || !p.contracts) continue;
        if (p.platform === 'polymarket') {
          if (p.side === 'yes') polyYes += p.contracts;
          else polyNo += p.contracts;
        } else if (p.platform === 'kalshi') {
          if (p.side === 'yes') kalshiYes += p.contracts;
          else kalshiNo += p.contracts;
        }
      }
      map.set(g._id, { polyYes, polyNo, kalshiYes, kalshiNo });
    }
    return map;
  }, [positionsData]);

  const handleSortClick = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
      } else {
        setSortKey(key);
        // Sums: ascending default (cheapest first). TNAs: descending
        // default (highest yield first). Name: ascending.
        if (key === 'name') setSortDir('asc');
        else if (SUM_KEYS.includes(key)) setSortDir('asc');
        else setSortDir('desc');
      }
    },
    [sortKey]
  );

  // ── Store-driven sort order ──────────────────────────────────────────
  // The high-frequency live state lives in the external store; the sorted
  // order recomputes on the store's throttled, rAF-coalesced cadence (not
  // on every WS tick / parent render). The comparator below operates on
  // the store's keys (pairId/ruleId) and reads each pair's live state via
  // `getKeySnapshot` — the SAME tier logic the old `sortedPairs` used:
  // market-data-enabled first, then default/name/metric within the tier.
  const comparator = useMemo<Comparator>(() => {
    return (ka, kb) => {
      const la = store.getKeySnapshot(ka);
      const lb = store.getKeySnapshot(kb);
      const enA = la?.pairEnabled === true;
      const enB = lb?.pairEnabled === true;
      // Tier 1: market-data-ENABLED pairs first (every sort).
      if (enA !== enB) return enA ? -1 : 1;

      const nameA = la?.pairName ?? '';
      const nameB = lb?.pairName ?? '';

      // Resting order: deterministic A→Z within the enabled tier.
      if (sortKey === 'default') return nameA.localeCompare(nameB);
      if (sortKey === 'name') {
        return sortDir === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }

      // Live metric sorts. Null metrics sink within the tier; ties fall
      // back to A→Z for a stable order instead of jitter on each frame.
      const mA = metricForSort(computePathMetrics(path, la), sortKey);
      const mB = metricForSort(computePathMetrics(path, lb), sortKey);
      if (mA == null && mB == null) return nameA.localeCompare(nameB);
      if (mA == null) return 1;
      if (mB == null) return -1;
      if (mA !== mB) return sortDir === 'desc' ? mB - mA : mA - mB;
      return nameA.localeCompare(nameB);
    };
  }, [store, sortKey, sortDir, path]);

  // Feed the comparator + freeze flag to the store; it throttles the
  // recompute and holds the order while frozen (cells keep ticking).
  useEffect(() => {
    store.setOrderInputs({ comparator, freeze: frozen });
  }, [store, comparator, frozen]);

  // Subscribe to the store's throttled order: `useLiveOrder` returns a new
  // array ref only when the order actually changes, so this re-renders the
  // tab (and re-runs the membership sort below) at the throttled cadence, not
  // on every WS tick. The array itself is unused — `store.rankOf` is the read.
  const orderTick = useLiveOrder(store);

  // Rows are driven by the manual-confirmed `pairs` list (react-query —
  // membership is authoritative there), ordered by the store's rank.
  // `store.rankOf` resolves EITHER the pairId or the ruleId to the same rank
  // (the store dual-keys both), and returns +Infinity for pairs not yet in
  // the store (pre-WS / pre-seed) so they tail-sort by name without vanishing.
  const displayedPairs = useMemo(() => {
    const rankOf = (pair: ManualConfirmedPair): number => {
      const rule = pair._id ? rulesByPairId.get(pair._id) : undefined;
      return store.rankOf(pair._id ?? rule?._id ?? '');
    };
    return [...pairs].sort((a, b) => {
      const ra = rankOf(a);
      const rb = rankOf(b);
      if (ra !== rb) return ra - rb;
      // Both unranked (not in store) — keep a deterministic A→Z tail.
      return (a.pairName ?? '').localeCompare(b.pairName ?? '');
    });
    // `orderTick` is the throttle trigger; `store.rankOf` is read live inside
    // the sort, gated by the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairs, rulesByPairId, store, orderTick]);

  /* ---------------------------------------------------------------- */
  /* Bulk market-data ON/OFF — multi-select + master toggle           */
  /* ---------------------------------------------------------------- */

  // Checked pair ids. Selection is per-tab state: it resets on tab
  // switch, which is fine — the locked-pair set is per-tab too, so a
  // selection never carries a pair the active tab can't manage.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkOpener = useBulkSetPairOpenerEnabled();

  // Rows whose checkbox is actually actionable: have an id.
  const selectableIds = useMemo(
    () => displayedPairs.map(p => p._id).filter((id): id is string => !!id),
    [displayedPairs]
  );

  // ─── Row virtualization (2026-05-23) ──────────────────────────────────
  // Mount only the visible `EntryRowByPath` instances + a top/bottom
  // spacer. Without this, hundreds of MUI `<TableRow>`s (each ~22
  // `<TableCell>` plus per-cell `sx={...}` + `<TextField>` / `<Switch>`
  // / `<Tooltip>`) were the dominant retained-memory cost — DOM nodes
  // grew linearly with the manual-confirmed-pair count and Emotion's
  // style cache piled up the per-row `sx` objects forever.
  //
  // EntryRowByPath now has an expandable `EntrySimulationBreakdownPanel`
  // that renders as a SECOND TableRow inside the row Fragment. Real row
  // heights vary (1- vs 2-line market titles, the expansion panel), so a
  // fixed/estimated size under-counted total height and the last rows fell
  // outside the scrollable range ("can't reach the bottom"). Each row now
  // reports its real height via `measureElement`, summing the expansion
  // sibling when open (see `measureRowWithExpansion`), so `getTotalSize()`
  // is exact. `estimateSize` stays as the pre-measurement seed only.
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: displayedPairs.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: index => {
      const pair = displayedPairs[index];
      const pairKey = pair?._id ?? pair?.pairName ?? String(index);
      // ~52px collapsed; ~320px when the breakdown panel is mounted.
      return expandedPairId === pairKey ? 320 : 52;
    },
    measureElement: measureRowWithExpansion,
    overscan: 8,
    getItemKey: index => {
      const pair = displayedPairs[index];
      return pair?._id ?? pair?.pairName ?? index;
    }
  });
  // Re-measure the mounted rows when the expanded pair changes. Toggling an
  // expansion mounts/unmounts a sibling `<TableRow>` WITHOUT resizing the
  // main row, so the ResizeObserver behind `measureElement` doesn't fire on
  // its own. Re-running `measureElement` over every mounted row refreshes
  // the newly-expanded row (now summing its panel) AND the just-collapsed
  // one (back to its bare height) — without wiping the measurement cache the
  // way `measure()` would. `useLayoutEffect` so it runs after the panel
  // commits but before paint.
  useLayoutEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    container
      .querySelectorAll<HTMLTableRowElement>('tr[data-index]')
      .forEach(node => rowVirtualizer.measureElement(node));
  }, [expandedPairId, rowVirtualizer]);
  const allSelected =
    selectableIds.length > 0 && selected.size >= selectableIds.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleSelect = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelected(prev =>
      prev.size >= selectableIds.length && selectableIds.length > 0
        ? new Set()
        : new Set(selectableIds)
    );
  }, [selectableIds]);

  // scope:'ids' — flip the checked subset. Backend caps pairIds at 100
  // per request, so chunk larger selections.
  const applyBulkSelected = useCallback(
    async (enabled: boolean) => {
      const ids = [...selected];
      if (ids.length === 0) return;
      setBulkBusy(true);
      try {
        for (let i = 0; i < ids.length; i += 100) {
          await bulkOpener.mutateAsync({
            scope: 'ids',
            pairIds: ids.slice(i, i + 100),
            enabled
          });
        }
        setSelected(new Set());
      } finally {
        setBulkBusy(false);
      }
    },
    [selected, bulkOpener]
  );

  // scope:'all' — flip every opener-eligible pair server-side in one
  // request (no id list to ship, no 100-cap to chunk around).
  const applyBulkAll = useCallback(
    async (enabled: boolean) => {
      setBulkBusy(true);
      try {
        await bulkOpener.mutateAsync({ scope: 'all', enabled });
        setSelected(new Set());
      } finally {
        setBulkBusy(false);
      }
    },
    [bulkOpener]
  );

  if (pairs.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
          No manually confirmed pairs found.
        </Typography>
      </Box>
    );
  }

  const sortLabelSx = {
    '&.MuiTableSortLabel-root': { color: 'inherit' },
    '&.Mui-active': { color: '#42a5f5' },
    '& .MuiTableSortLabel-icon': { fontSize: '0.8rem' },
    // Show the direction arrow on EVERY sortable label, even when
    // inactive — dimmed at 40% so the active sort still pops. Lets
    // the operator see all sortable handles at a glance instead of
    // only the currently-active one.
    '&:not(.Mui-active) .MuiTableSortLabel-icon': {
      opacity: 0.4
    }
  } as const;

  // Per-column $ / % badge. Active target gets a light blue background +
  // matching text so the operator can tell which of the two is the sort
  // key at a glance — replaces the per-label arrows the column used to
  // render.
  const sortBadgeSx = (active: boolean) =>
    ({
      cursor: 'pointer',
      px: 0.4,
      borderRadius: 0.5,
      color: active ? '#42a5f5' : 'inherit',
      backgroundColor: active ? 'rgba(66, 165, 245, 0.18)' : 'transparent',
      '&:hover': { color: '#42a5f5' }
    }) as const;

  const polySideLabel = path === 'a' ? 'YES' : 'NO';
  const kalshiSideLabel = path === 'a' ? 'NO' : 'YES';

  return (
    <Box
      sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      <BulkActionBar
        totalCount={selectableIds.length}
        selectedCount={selected.size}
        busy={bulkBusy}
        onEnableSelected={() => void applyBulkSelected(true)}
        onDisableSelected={() => void applyBulkSelected(false)}
        onClearSelection={() => setSelected(new Set())}
        onEnableAll={() => void applyBulkAll(true)}
        onDisableAll={() => void applyBulkAll(false)}
      />
      <TableContainer
        ref={tableContainerRef}
        sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{ tableLayout: 'fixed', width: '100%' }}
        >
          <colgroup>
            {COL_WIDTHS_PX.map((w, i) => (
              <col key={i} style={{ width: `${(w / COL_TOTAL_PX) * 100}%` }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...gSx,
                  width: 18,
                  px: 0,
                  verticalAlign: 'middle',
                  top: 0,
                  backgroundColor: '#1a1a2e'
                }}
                rowSpan={2}
                align="center"
                padding="none"
              >
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleSelectAll}
                  disabled={selectableIds.length === 0 || bulkBusy}
                  sx={{
                    p: 0,
                    color: 'grey.500',
                    '& .MuiSvgIcon-root': { fontSize: 14 }
                  }}
                />
              </TableCell>
              <TableCell
                sx={{ ...gSx, width: 480, top: 0, backgroundColor: '#1a1a2e' }}
                rowSpan={2}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={0.5}
                >
                  <TableSortLabel
                    active={sortKey === 'name'}
                    direction={sortKey === 'name' ? sortDir : 'asc'}
                    onClick={() => handleSortClick('name')}
                    sx={sortLabelSx}
                  >
                    Market
                  </TableSortLabel>
                  <Tooltip
                    title={
                      frozen
                        ? 'Sort frozen — row order is held. Click to resume live sorting.'
                        : 'Freeze sort — hold the current row order so it stops reshuffling on price updates.'
                    }
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      onClick={() => setFrozen(f => !f)}
                      aria-label={
                        frozen ? 'resume live sorting' : 'freeze sort'
                      }
                      sx={{ p: 0.1, color: frozen ? '#42a5f5' : 'grey.600' }}
                    >
                      <AcUnitIcon sx={{ fontSize: '0.85rem' }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </TableCell>
              <TableCell
                sx={{ ...gSx, width: 56, top: 0, backgroundColor: '#1a1a2e' }}
                rowSpan={2}
                align="center"
                title="Open contracts held on the YES side (Poly on top, Kalshi below)."
              >
                YES
              </TableCell>
              <TableCell
                sx={{ ...gSx, width: 56, top: 0, backgroundColor: '#1a1a2e' }}
                rowSpan={2}
                align="center"
                title="Open contracts held on the NO side (Poly on top, Kalshi below)."
              >
                NO
              </TableCell>
              {/* Eng — the engine's IN-MEMORY contracts bought this session, per
                thread. A bidirectional rule runs two paths × two legs = up to
                four threads; here each path (A / B) shows its Poly (top) and
                Kalshi (bottom) session count. Live-only (blank until the engine
                emits). NOT the same as the synced held YES/NO columns. */}
              <TableCell
                sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
                colSpan={2}
                align="center"
                title="Engine session contracts bought per thread (Poly top / Kalshi bottom), split by arb path A and B — the live in-memory count, not the synced position."
              >
                Eng
              </TableCell>
              <TableCell
                sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
                colSpan={4}
              >
                Polymarket {polySideLabel}
              </TableCell>
              <TableCell
                sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
                colSpan={4}
              >
                Kalshi {kalshiSideLabel}
              </TableCell>
              <TableCell
                sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
                colSpan={3}
              >
                Parameters
              </TableCell>
              {/* Per-strategy groups — each spans 2 sub-cells: [legs] + [$/%].
                Legs show maker leg (blue) + taker leg (orange) on the two
                ACTUAL platforms the strategy uses, so the operator never has
                to mentally combine prices from different columns to read a
                strategy. Mirrors the unwinder's Sim-P-Mk / Sim-K-Mk layout
                (UnwindingTabSumOfSides). */}
              <TableCell
                sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
                colSpan={2}
                title="Taker-Taker: both legs fire as taker (orange = all-in price each leg pays)."
              >
                TT
              </TableCell>
              <TableCell
                sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
                colSpan={2}
                title="Poly maker: REST on Polymarket (blue) + TAKE on Kalshi (orange)."
              >
                P Mk
              </TableCell>
              <TableCell
                sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
                colSpan={2}
                title="Kalshi maker: REST on Kalshi (blue) + TAKE on Polymarket (orange)."
              >
                K Mk
              </TableCell>
              <TableCell
                sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
                colSpan={3}
              >
                Ctrl
              </TableCell>
            </TableRow>
            <TableRow>
              {/* Eng sub-headers — one column per arb path. */}
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 54,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="center"
                title="Path A: Poly-YES + Kalshi-NO"
              >
                A
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 54,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="center"
                title="Path B: Poly-NO + Kalshi-YES"
              >
                B
              </TableCell>
              {/* Polymarket — Qty · Bid · Ask · Qty */}
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 24,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Qty
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 32,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Bid
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 32,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Ask
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 24,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Qty
              </TableCell>
              {/* Kalshi — Qty · Bid · Ask · Qty */}
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 24,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Qty
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 32,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Bid
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 32,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Ask
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 24,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Qty
              </TableCell>
              {/* Parameters */}
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 40,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Thr
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 30,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Size
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 36,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                MaxV
              </TableCell>
              {/* Sim sub-row — per-strategy pair: [Legs placeholder] + [$/% sort].
                Legs cell has no sort handle; the $/% cell keeps the existing
                two-target sort UI ($ → sum, % → tna, arrow toggles direction).
                Strategy name (TT / P Mk / K Mk) lives in the group header row
                above, spanning both sub-cells. */}
              {(
                [
                  {
                    sumKey: 'sumTT',
                    tnaKey: 'tnaTT',
                    tip: 'Taker-Taker — click $ or % to switch sort target; arrow toggles direction.'
                  },
                  {
                    sumKey: 'sumPolyMk',
                    tnaKey: 'tnaPolyMk',
                    tip: 'Poly maker — click $ or % to switch sort target; arrow toggles direction.'
                  },
                  {
                    sumKey: 'sumKalshiMk',
                    tnaKey: 'tnaKalshiMk',
                    tip: 'Kalshi maker — click $ or % to switch sort target; arrow toggles direction.'
                  }
                ] as const
              ).map(({ sumKey, tnaKey, tip }) => {
                const sumActive = sortKey === sumKey;
                const tnaActive = sortKey === tnaKey;
                const colActive = sumActive || tnaActive;
                const ArrowIcon =
                  colActive && sortDir === 'asc'
                    ? ArrowUpwardIcon
                    : ArrowDownwardIcon;
                // Arrow click: toggle direction when this column is the
                // active sort; otherwise activate the $ sort for this
                // column (matches the default-direction logic in
                // handleSortClick).
                const arrowKey = tnaActive ? tnaKey : sumKey;
                return (
                  <Fragment key={sumKey}>
                    {/* Legs placeholder — no sort. Width matches the body
                      legs cell below. */}
                    <TableCell
                      sx={{
                        ...hSx,
                        ...subHSx,
                        width: 50,
                        top: ROW1_HEIGHT,
                        backgroundColor: '#1a1a2e'
                      }}
                    />
                    <TableCell
                      sx={{
                        ...hSx,
                        ...subHSx,
                        width: 50,
                        top: ROW1_HEIGHT,
                        backgroundColor: '#1a1a2e'
                      }}
                      align="right"
                      title={tip}
                    >
                      <Stack
                        direction="row"
                        spacing={0.2}
                        justifyContent="flex-end"
                        alignItems="center"
                      >
                        <Box
                          component="span"
                          onClick={() => handleSortClick(sumKey)}
                          sx={sortBadgeSx(sumActive)}
                        >
                          $
                        </Box>
                        <ArrowIcon
                          onClick={() => handleSortClick(arrowKey)}
                          sx={{
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            color: colActive ? '#42a5f5' : 'inherit',
                            opacity: colActive ? 1 : 0.4
                          }}
                        />
                        <Box
                          component="span"
                          onClick={() => handleSortClick(tnaKey)}
                          sx={sortBadgeSx(tnaActive)}
                        >
                          %
                        </Box>
                      </Stack>
                    </TableCell>
                  </Fragment>
                );
              })}
              {/* Ctrl — 3 switches: PolyMk · KalshiMk · TT */}
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 28,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="center"
                title="Poly maker"
              >
                P
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 28,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="center"
                title="Kalshi maker"
              >
                K
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 28,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="center"
                title="Taker-Taker"
              >
                TT
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              const virtualItems = rowVirtualizer.getVirtualItems();
              const totalSize = rowVirtualizer.getTotalSize();
              const paddingTop =
                virtualItems.length > 0 ? virtualItems[0].start : 0;
              const paddingBottom =
                virtualItems.length > 0
                  ? totalSize - virtualItems[virtualItems.length - 1].end
                  : 0;
              // Spacer rows preserve the scrollbar size; `colSpan={100}` is
              // a known-safe "all columns" idiom — the browser clamps to
              // the actual count and the cell stays invisible.
              return (
                <>
                  {paddingTop > 0 && (
                    <TableRow style={{ height: paddingTop }}>
                      <TableCell
                        colSpan={100}
                        sx={{ p: 0, border: 'none' }}
                        aria-hidden
                      />
                    </TableRow>
                  )}
                  {virtualItems.map(vi => {
                    const pair = displayedPairs[vi.index];
                    const rule = pair._id
                      ? rulesByPairId.get(pair._id)
                      : undefined;
                    const anyRule = pair._id
                      ? anyRulesByPairId.get(pair._id)
                      : undefined;
                    const pairKey = pair._id ?? pair.pairName;
                    return (
                      <EntryRowByPath
                        key={pairKey}
                        dataIndex={vi.index}
                        measureRef={rowVirtualizer.measureElement}
                        path={path}
                        pair={pair}
                        rule={rule}
                        anyRule={anyRule}
                        store={store}
                        pairKey={pairKey}
                        isExpanded={expandedPairId === pairKey}
                        onToggleExpand={handleToggleExpand}
                        selected={pair._id ? selected.has(pair._id) : false}
                        onToggleSelect={toggleSelect}
                        contracts={
                          pair._id
                            ? (contractsByPairId.get(pair._id) ??
                              ZERO_CONTRACTS)
                            : ZERO_CONTRACTS
                        }
                        onCreateRule={onCreateRule}
                        onSetMakerFlag={onSetMakerFlag}
                        onUpdateRule={onUpdateRule}
                        onHidePair={onHidePair}
                        onOpenEnginePosition={onOpenEnginePosition}
                      />
                    );
                  })}
                  {paddingBottom > 0 && (
                    <TableRow style={{ height: paddingBottom }}>
                      <TableCell
                        colSpan={100}
                        sx={{ p: 0, border: 'none' }}
                        aria-hidden
                      />
                    </TableRow>
                  )}
                </>
              );
            })()}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Simulation Breakdown Panel                                          */
/* ------------------------------------------------------------------ */
//
// Mirror of the unwinder's `SimulationBreakdownPanel` (UnwindingPage.tsx),
// inverted for BUY (entry) semantics. Renders ONLY what the Rust engine
// emits on `EnrichedEntryStatus.simulation` — no FE-local fee math. The
// panel is empty ("No data") until the engine populates `breakdown`.

const stepLabel = { fontSize: '0.6rem', color: 'grey.500' } as const;
const stepValue = { fontSize: '0.62rem', fontFamily: 'monospace' } as const;

const EntrySimulationBreakdownPanel = ({
  polyAsMaker,
  kalshiAsMaker,
  expiryDate,
  daysToExpiry
}: {
  polyAsMaker: EntrySimulationResult | null;
  kalshiAsMaker: EntrySimulationResult | null;
  expiryDate?: string | Date | null;
  daysToExpiry?: number | null;
}) => {
  const renderDirection = (
    label: string,
    result: EntrySimulationResult | null
  ) => {
    if (!result?.breakdown)
      return (
        <Box sx={{ flex: 1, p: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', color: 'grey.500' }}>
            No data
          </Typography>
        </Box>
      );
    const b = result.breakdown;
    return (
      <Box sx={{ flex: 1, p: 1 }}>
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            mb: 0.5,
            color: '#42a5f5'
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '2px 8px'
          }}
        >
          <Typography sx={stepLabel}>1. Raw taker ask</Typography>
          <Typography sx={stepValue}>{fmtPrice(b.rawTakerAsk)}</Typography>

          <Typography sx={stepLabel}>
            2. Taker fee ({b.takerPlatform}, {(b.takerFeeRate * 100).toFixed(2)}
            %)
          </Typography>
          <Typography sx={stepValue}>+{fmtPrice(b.takerFeeAmount)}</Typography>

          <Typography sx={stepLabel}>3. Net taker ask</Typography>
          <Typography sx={stepValue}>{fmtPrice(b.netTakerAsk)}</Typography>

          <Typography sx={stepLabel}>
            4. Max buy price (TNA={b.targetTna}%, {b.effectiveDays.toFixed(1)}d)
          </Typography>
          <Typography sx={stepValue}>{fmtPrice(b.maxBuyPrice)}</Typography>

          <Typography sx={stepLabel}>5. Best bid on maker book</Typography>
          <Typography sx={stepValue}>
            {fmtPrice(b.makerBidTopPrice)} ({Math.round(b.makerBidTopSize)}{' '}
            lots)
          </Typography>

          <Typography sx={stepLabel}>
            6. Maker price (at max buy price
            {b.joinedExistingLevel ? ', joined' : ''})
          </Typography>
          <Typography sx={{ ...stepValue, fontWeight: 600, color: '#42a5f5' }}>
            {fmtPrice(b.makerPrice)}
          </Typography>

          <Typography sx={stepLabel}>
            7. Maker fee ({(b.makerFeeRate * 100).toFixed(2)}%)
          </Typography>
          <Typography sx={stepValue}>+{fmtPrice(b.makerFeeAmount)}</Typography>

          <Typography sx={stepLabel}>8. Net maker cost</Typography>
          <Typography sx={stepValue}>{fmtPrice(b.netMakerPrice)}</Typography>

          <Typography sx={stepLabel}>
            9. Size (req={b.requestedSize}, taker=
            {Math.round(b.takerLiquidityCap)}, pos={b.pairCap || '∞'})
          </Typography>
          <Typography sx={stepValue}>{b.effectiveSize} contracts</Typography>

          <Typography sx={stepLabel}>10. Achieved TNA</Typography>
          <Typography
            sx={{
              ...stepValue,
              fontWeight: 600,
              color: b.achievedTna >= 0 ? '#4caf50' : '#f44336'
            }}
          >
            {b.achievedTna.toFixed(2)}%
          </Typography>
        </Box>
        {b.unplaceableNoMillis && (
          <Typography
            sx={{
              fontSize: '0.62rem',
              color: '#ff9800',
              mt: 0.5,
              fontWeight: 600
            }}
          >
            Not placeable: maxBuyPrice &lt; 0.01 and market has no millis tick
            support
          </Typography>
        )}
      </Box>
    );
  };

  const expiryStr = expiryDate
    ? new Date(expiryDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : 'unknown';
  const daysStr =
    daysToExpiry != null ? `${daysToExpiry.toFixed(1)} days` : 'unknown';

  return (
    <Box
      sx={{
        py: 1,
        px: 2,
        backgroundColor: 'rgba(255,255,255,0.02)',
        borderRadius: 1
      }}
    >
      {/* Expiry header */}
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          mb: 1,
          pb: 0.5,
          borderBottom: '1px solid rgba(255,255,255,0.06)'
        }}
      >
        <Typography sx={{ fontSize: '0.65rem', color: 'grey.400' }}>
          <b>Expiry:</b> {expiryStr}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.65rem',
            color:
              daysToExpiry != null && daysToExpiry < 1 ? '#f44336' : 'grey.400'
          }}
        >
          <b>Days to expiry:</b> {daysStr}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: 'grey.400' }}>
          <b>Effective days:</b>{' '}
          {polyAsMaker?.breakdown?.effectiveDays?.toFixed(1) ??
            kalshiAsMaker?.breakdown?.effectiveDays?.toFixed(1) ??
            'N/A'}
        </Typography>
      </Box>
      {/* Two-column breakdown */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {renderDirection('Polymarket as Maker', polyAsMaker)}
        <Box sx={{ width: '1px', backgroundColor: 'rgba(255,255,255,0.08)' }} />
        {renderDirection('Kalshi as Maker', kalshiAsMaker)}
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Row                                                                */
/* ------------------------------------------------------------------ */

interface RowProps {
  /** Virtual-row index — drives `data-index` for react-virtual's measurer. */
  dataIndex: number;
  /** react-virtual's `measureElement` ref callback; lands on the main row. */
  measureRef: (el: HTMLTableRowElement | null) => void;
  path: 'a' | 'b';
  pair: ManualConfirmedPair;
  rule?: EntryRule;
  anyRule?: EntryRule;
  /** Per-pair external store. The row subscribes to its OWN key
   *  (`pair._id ?? rule._id`) so only it re-renders on its WS frame. */
  store: LiveStore<EnrichedEntryStatus>;
  /** Whether this row's bulk-select checkbox is ticked. */
  selected: boolean;
  /** Toggle this pair in the bulk-select set. */
  onToggleSelect: (id: string) => void;
  contracts: PairContracts;
  onCreateRule: (params: CreateEntrySumRuleParams) => Promise<void>;
  onSetMakerFlag: (
    rule: EntryRule,
    flag: MakerFlag,
    value: boolean,
    clearOthers?: MakerFlag[]
  ) => Promise<void> | void;
  onUpdateRule: (rule: EntryRule, dto: UpdateEntryRuleDto) => Promise<boolean>;
  onHidePair: (pair: ManualConfirmedPair) => Promise<void> | void;
  /** Opens the "Engine position" drawer for this row's rule. */
  onOpenEnginePosition: (ruleId: string, pairName: string) => void;
  /** Stable key (pair id, else pair name) identifying this row for the
   *  shared expanded-row state on the tab. */
  pairKey: string;
  /** Whether the per-rule TNA simulation breakdown is expanded. */
  isExpanded: boolean;
  /** Toggle this row's simulation breakdown open/closed. */
  onToggleExpand: (pairKey: string) => void;
}

const EntryRowByPath = memo(
  ({
    dataIndex,
    measureRef,
    path,
    pair,
    rule,
    anyRule,
    store,
    pairKey,
    isExpanded,
    onToggleExpand,
    selected,
    onToggleSelect,
    contracts,
    onCreateRule,
    onSetMakerFlag,
    onUpdateRule,
    onHidePair,
    onOpenEnginePosition
  }: RowProps) => {
    // Subscribe to THIS pair's live state — only this row re-renders when
    // its pair's WS frame lands. Look up by pairId first, falling back to
    // ruleId (mirrors the legacy `pairId ?? ruleId` resolution).
    const liveState = useLiveState(store, pair._id ?? rule?._id ?? '');
    const ruleSize =
      rule?.kalshiConfig?.contractsToBuy ??
      rule?.polymarketConfig?.contractsToBuy ??
      DEFAULT_SIZE;
    // Each tab edits its OWN path's threshold; fall back to the rule-level
    // seed, then the default.
    const perPathThreshold =
      path === 'a' ? rule?.pathA?.thresholdSum : rule?.pathB?.thresholdSum;
    const initThreshold =
      perPathThreshold != null
        ? String(perPathThreshold)
        : rule?.thresholdSum != null
          ? String(rule.thresholdSum)
          : String(DEFAULT_THRESHOLD);
    const initSize = String(ruleSize);
    const initMaxVol = String(rule?.maxVolumeContracts ?? DEFAULT_MAX_VOL);

    const [threshold, setThreshold] = useState(initThreshold);
    const [size, setSize] = useState(initSize);
    const [maxVol, setMaxVol] = useState(initMaxVol);
    const [savedThreshold, setSavedThreshold] = useState(initThreshold);
    const [savedSize, setSavedSize] = useState(initSize);
    const [savedMaxVol, setSavedMaxVol] = useState(initMaxVol);
    const [thresholdError, setThresholdError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [togglingFlag, setTogglingFlag] = useState<MakerFlag | null>(null);
    const [togglingTT, setTogglingTT] = useState(false);
    const [hidePending, setHidePending] = useState(false);
    const [hedgeOpen, setHedgeOpen] = useState(false);

    // Per-pair opener kill switch. Default OFF for manual-confirmed pairs;
    // the operator turns it on to start streaming WS data + arming rules.
    // No confirmation dialog here — turning off has no live position to
    // worry about (the opener never holds positions; entries settle into
    // the unwinder), unlike the unwinder side.
    const pairOpenerOn = liveState?.pairEnabled === true;
    const setPairOpenerEnabled = useSetPairOpenerEnabled();

    useEffect(() => {
      if (!rule) return;
      const dirty =
        threshold !== savedThreshold ||
        size !== savedSize ||
        maxVol !== savedMaxVol;
      if (dirty) return;
      const perPath =
        path === 'a' ? rule.pathA?.thresholdSum : rule.pathB?.thresholdSum;
      const nextThreshold = perPath != null ? perPath : rule.thresholdSum;
      if (nextThreshold != null) {
        const next = String(nextThreshold);
        if (next !== savedThreshold) {
          setThreshold(next);
          setSavedThreshold(next);
        }
      }
      const ruleSizeNow =
        rule.kalshiConfig?.contractsToBuy ??
        rule.polymarketConfig?.contractsToBuy;
      if (ruleSizeNow != null) {
        const next = String(ruleSizeNow);
        if (next !== savedSize) {
          setSize(next);
          setSavedSize(next);
        }
      }
      const nextMax = String(rule.maxVolumeContracts);
      if (nextMax !== savedMaxVol) {
        setMaxVol(nextMax);
        setSavedMaxVol(nextMax);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      rule?.thresholdSum,
      rule?.pathA?.thresholdSum,
      rule?.pathB?.thresholdSum,
      rule?.maxVolumeContracts,
      rule?.kalshiConfig?.contractsToBuy,
      rule?.polymarketConfig?.contractsToBuy
    ]);

    const isDirty =
      threshold !== savedThreshold ||
      size !== savedSize ||
      maxVol !== savedMaxVol;

    const validateThreshold = useCallback((value: string): string | null => {
      const n = Number.parseFloat(value);
      if (!Number.isFinite(n) || n <= 0) return 'Must be > 0';
      if (n >= 1) return 'Must be < 1.00 (hard cap)';
      return null;
    }, []);

    useEffect(() => {
      setThresholdError(validateThreshold(threshold));
    }, [threshold, validateThreshold]);

    const handleSave = useCallback(async () => {
      if (!isDirty || !rule) return;
      const tErr = validateThreshold(threshold);
      if (tErr) {
        setThresholdError(tErr);
        return;
      }
      const parsedThreshold = Number.parseFloat(threshold);
      const parsedSize = Number.parseInt(size, 10);
      const parsedMaxVol = Number.parseFloat(maxVol);
      const dto: UpdateEntryRuleDto = {};
      if (
        Number.isFinite(parsedThreshold) &&
        parsedThreshold > 0 &&
        parsedThreshold < 1 &&
        threshold !== savedThreshold
      ) {
        // Edit THIS tab's path threshold only — never the shared rule-level
        // value (the two tabs would otherwise collide).
        if (path === 'a') dto.pathAThresholdSum = parsedThreshold;
        else dto.pathBThresholdSum = parsedThreshold;
      }
      if (
        Number.isFinite(parsedMaxVol) &&
        parsedMaxVol > 0 &&
        maxVol !== savedMaxVol
      ) {
        dto.maxVolumeContracts = parsedMaxVol;
      }
      if (Number.isFinite(parsedSize) && parsedSize > 0 && size !== savedSize) {
        if (rule.kalshiConfig) {
          dto.kalshiConfig = {
            side: rule.kalshiConfig.side,
            ticker: rule.kalshiConfig.ticker,
            contractsToBuy: parsedSize
          };
        }
        if (rule.polymarketConfig) {
          dto.polymarketConfig = {
            side: rule.polymarketConfig.side,
            yesTokenId: rule.polymarketConfig.yesTokenId,
            noTokenId: rule.polymarketConfig.noTokenId,
            conditionId: rule.polymarketConfig.conditionId,
            contractsToBuy: parsedSize
          };
        }
      }
      setSaving(true);
      const ok = await onUpdateRule(rule, dto);
      if (ok) {
        setSavedThreshold(threshold);
        setSavedSize(size);
        setSavedMaxVol(maxVol);
      }
      setSaving(false);
    }, [
      isDirty,
      path,
      threshold,
      size,
      maxVol,
      savedThreshold,
      savedSize,
      savedMaxVol,
      rule,
      onUpdateRule,
      validateThreshold
    ]);

    // Pre-rule commit: typing values with no rule creates a PAUSED rule
    // carrying those values. No maker flag is set — the row lands paused
    // with all 3 switches visually off (rule flags are all false on
    // creation). The operator later activates via a switch.
    const handleCreatePausedRule = useCallback(async () => {
      if (!isDirty || rule || !pair._id) return;
      const tErr = validateThreshold(threshold);
      if (tErr) {
        setThresholdError(tErr);
        return;
      }
      const parsedThr = Number.parseFloat(threshold);
      const parsedSize = Number.parseInt(size, 10);
      const parsedMaxV = Number.parseFloat(maxVol);
      if (!Number.isFinite(parsedThr) || parsedThr <= 0 || parsedThr >= 1)
        return;
      if (!Number.isFinite(parsedSize) || parsedSize <= 0) return;
      if (!Number.isFinite(parsedMaxV) || parsedMaxV <= 0) return;
      setSaving(true);
      try {
        await onCreateRule({
          pair,
          makerFlag: null,
          tabPath: path,
          thresholdSum: parsedThr,
          size: parsedSize,
          maxVolumeContracts: parsedMaxV
        });
        setSavedThreshold(threshold);
        setSavedSize(size);
        setSavedMaxVol(maxVol);
      } finally {
        setSaving(false);
      }
    }, [
      isDirty,
      rule,
      pair,
      path,
      threshold,
      size,
      maxVol,
      validateThreshold,
      onCreateRule
    ]);

    const handleCommit = useCallback(async () => {
      if (rule) {
        await handleSave();
      } else {
        await handleCreatePausedRule();
      }
    }, [rule, handleSave, handleCreatePausedRule]);

    // Every rule now owns BOTH arb paths (the bidirectional toggle + the
    // cross-tab lock were removed). All 4 maker switches bind to live rule
    // flags on BOTH tabs, with no path-classification narrowing.
    const ruleBelongsToThisTab = true;

    // Market data + math — always tracks the TAB's path, never a
    // "lower-sum" hint. The other tab owns the other path. All sim
    // numbers come straight from the backend `engineSim` payload.
    const metrics = computePathMetrics(path, liveState);
    const {
      polyAsk,
      polyBid,
      kalshiAsk,
      kalshiBid,
      polyAllIn,
      kalshiAllIn,
      polyMakerRest,
      kalshiMakerRest,
      sumTT,
      sumPolyMk,
      sumKalshiMk,
      tnaTT,
      tnaPolyMk,
      tnaKalshiMk
    } = metrics;

    // Qty cells follow the same kill-switch gate as the price metrics:
    // when the opener is off for this pair, show no size either.
    const qtyState = pairOpenerOn ? liveState : undefined;
    const polyAskSize = (
      path === 'a' ? qtyState?.polyYesAsk : qtyState?.polyNoAsk
    )?.size;
    const polyBidSize = (
      path === 'a' ? qtyState?.polyYesBid : qtyState?.polyNoBid
    )?.size;
    const kalshiAskSize = (
      path === 'a' ? qtyState?.kalshiNoAsk : qtyState?.kalshiYesAsk
    )?.size;
    const kalshiBidSize = (
      path === 'a' ? qtyState?.kalshiNoBid : qtyState?.kalshiYesBid
    )?.size;

    const thresholdNum = Number.parseFloat(threshold);
    const thresholdValid =
      Number.isFinite(thresholdNum) && thresholdNum > 0 && thresholdNum < 1;

    // Switch → flag mapping for this tab's path.
    const polyFlag = TAB_FLAGS[path].poly;
    const kalshiFlag = TAB_FLAGS[path].kalshi;

    // Switch display state: only bind to rule flags if the rule belongs
    // to this tab's path. Off otherwise — the OTHER tab owns it.
    const polyMakerOn = ruleBelongsToThisTab && rule?.[polyFlag] === true;
    const kalshiMakerOn = ruleBelongsToThisTab && rule?.[kalshiFlag] === true;
    const ttOn =
      ruleBelongsToThisTab &&
      (anyRule?.entryStrategy ?? 'maker-first') === 'taker-taker';

    const inSx = {
      '& .MuiInputBase-input': {
        fontSize: '0.62rem',
        py: 0.15,
        px: 0.2,
        textAlign: 'right',
        width: 28
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255,255,255,0.08)'
      },
      minWidth: 0
    } as const;

    // The red hard-cap row outline is gone. Hover highlight stays.
    const rowSx = { '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } };

    // Apply a maker-flag change. Every rule owns both arb paths now, so
    // arming one leg NEVER clears the other path's flags — all 4 maker
    // switches are independently armable.
    const doSetMakerFlag = useCallback(
      async (flag: MakerFlag, value: boolean) => {
        setTogglingFlag(flag);
        try {
          if (rule) {
            await onSetMakerFlag(rule, flag, value, []);
          } else if (value) {
            const tErr = validateThreshold(threshold);
            if (tErr) {
              setThresholdError(tErr);
              return;
            }
            const parsedThr = Number.parseFloat(threshold);
            const parsedSize = Number.parseInt(size, 10);
            const parsedMaxV = Number.parseFloat(maxVol);
            await onCreateRule({
              pair,
              makerFlag: flag,
              tabPath: path,
              thresholdSum:
                Number.isFinite(parsedThr) && parsedThr > 0 && parsedThr < 1
                  ? parsedThr
                  : DEFAULT_THRESHOLD,
              size:
                Number.isFinite(parsedSize) && parsedSize > 0
                  ? parsedSize
                  : DEFAULT_SIZE,
              maxVolumeContracts:
                Number.isFinite(parsedMaxV) && parsedMaxV > 0
                  ? parsedMaxV
                  : DEFAULT_MAX_VOL
            });
          }
        } finally {
          // Clear the spinner the moment the mutation settles — the switch
          // (now showing the optimistically-patched flag) returns. The old
          // blind 2s timer cleared independently of the round-trip, so it
          // either lingered after a fast response or snapped back to the
          // stale pre-toggle state before the refetch landed.
          setTogglingFlag(null);
        }
      },
      [
        rule,
        threshold,
        size,
        maxVol,
        pair,
        path,
        onCreateRule,
        onSetMakerFlag,
        validateThreshold
      ]
    );

    const handleTTToggle = useCallback(
      async (value: boolean) => {
        setTogglingTT(true);
        try {
          if (anyRule) {
            // TT is a per-rule entry-strategy toggle, independent of path.
            await onUpdateRule(anyRule, {
              entryStrategy: value ? 'taker-taker' : 'maker-first'
            });
            return;
          }
          if (!value) return;
          const tErr = validateThreshold(threshold);
          if (tErr) {
            setThresholdError(tErr);
            return;
          }
          const parsedThr = Number.parseFloat(threshold);
          const parsedSize = Number.parseInt(size, 10);
          const parsedMaxV = Number.parseFloat(maxVol);
          await onCreateRule({
            pair,
            makerFlag: null,
            tabPath: path,
            entryStrategy: 'taker-taker',
            thresholdSum:
              Number.isFinite(parsedThr) && parsedThr > 0 && parsedThr < 1
                ? parsedThr
                : DEFAULT_THRESHOLD,
            size:
              Number.isFinite(parsedSize) && parsedSize > 0
                ? parsedSize
                : DEFAULT_SIZE,
            maxVolumeContracts:
              Number.isFinite(parsedMaxV) && parsedMaxV > 0
                ? parsedMaxV
                : DEFAULT_MAX_VOL
          });
        } finally {
          setTimeout(() => setTogglingTT(false), 2000);
        }
      },
      [
        anyRule,
        pair,
        path,
        threshold,
        size,
        maxVol,
        onCreateRule,
        onUpdateRule,
        validateThreshold
      ]
    );

    const ruleStatusColor =
      anyRule?.status === 'active'
        ? '#4caf50'
        : anyRule?.status === 'paused'
          ? '#ff9800'
          : 'grey.600';

    // Sum cell color: red if over hard cap (informational only — the
    // backend gate is the safety contract); green if at or below the
    // operator's threshold; default blue otherwise.
    const sumColor = (sum: number | null): string => {
      if (sum == null) return 'grey.500';
      if (sum >= ENTRY_HARD_CAP_GROSS) return '#f44336';
      if (thresholdValid && sum <= thresholdNum) return '#4caf50';
      return '#42a5f5';
    };
    const tnaColor = (tna: number | null): string => {
      if (tna == null) return 'grey.500';
      return tna >= 0 ? '#4caf50' : '#f44336';
    };

    const polyCategoryFeePct = (
      polymarketTakerFeeRate(rule?.category) * 100
    ).toFixed(1);

    return (
      <>
        <TableRow ref={measureRef} data-index={dataIndex} sx={rowSx}>
          {/* Bulk select */}
          <TableCell
            sx={{ ...cSx, px: 0, verticalAlign: 'middle' }}
            align="center"
            padding="none"
            onClick={e => e.stopPropagation()}
          >
            <Checkbox
              size="small"
              checked={selected}
              disabled={!pair._id}
              onChange={() => pair._id && onToggleSelect(pair._id)}
              sx={{
                p: 0,
                color: 'grey.600',
                '& .MuiSvgIcon-root': { fontSize: 14 }
              }}
            />
          </TableCell>
          {/* Market */}
          <TableCell sx={{ ...cSx, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.2 }}>
              {/* Expand the per-rule TNA simulation breakdown. */}
              <Box
                onClick={e => {
                  e.stopPropagation();
                  onToggleExpand(pairKey);
                }}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexShrink: 0
                }}
              >
                {isExpanded ? (
                  <KeyboardArrowDownIcon
                    sx={{ fontSize: 14, mt: 0.2, color: 'grey.500' }}
                  />
                ) : (
                  <KeyboardArrowRightIcon
                    sx={{ fontSize: 14, mt: 0.2, color: 'grey.500' }}
                  />
                )}
              </Box>
              <Tooltip
                title={
                  anyRule
                    ? `Entry rule: ${anyRule.status}`
                    : 'No rule yet — toggle a switch to create'
                }
                placement="top"
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: ruleStatusColor,
                    mt: 0.5,
                    ml: 0.2,
                    flexShrink: 0
                  }}
                />
              </Tooltip>
              <Tooltip
                title={
                  pairOpenerOn
                    ? 'Pair ON — opener is streaming market data and rules can be activated.'
                    : 'Pair OFF — no market data, rules cannot be activated. Click to turn on.'
                }
                placement="top"
              >
                <span>
                  <Switch
                    size="small"
                    checked={pairOpenerOn}
                    disabled={setPairOpenerEnabled.isLoading || !pair._id}
                    onChange={e => {
                      e.stopPropagation();
                      if (!pair._id) return;
                      setPairOpenerEnabled.mutate({
                        pairId: pair._id,
                        enabled: e.target.checked
                      });
                    }}
                    onClick={e => e.stopPropagation()}
                    disableRipple
                    sx={{
                      m: 0,
                      p: 0,
                      width: 18,
                      height: 10,
                      display: 'inline-flex',
                      '& .MuiSwitch-switchBase': {
                        p: '1px',
                        '&.Mui-checked': {
                          transform: 'translateX(8px)',
                          color: '#fff',
                          '& + .MuiSwitch-track': {
                            backgroundColor: '#7c3aed',
                            opacity: 1
                          }
                        }
                      },
                      '& .MuiSwitch-thumb': {
                        width: 8,
                        height: 8,
                        boxShadow: 'none'
                      },
                      '& .MuiSwitch-track': {
                        borderRadius: 5,
                        backgroundColor: 'rgba(255,255,255,0.18)',
                        opacity: 1
                      }
                    }}
                  />
                </span>
              </Tooltip>
              {/* Engine believed-position drawer — what the live engine
                  thinks we hold right now. Enabled once a rule exists. */}
              <Tooltip
                title={rule?._id ? 'Engine position' : 'No rule yet'}
                placement="top"
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={!rule?._id}
                    onClick={e => {
                      e.stopPropagation();
                      if (rule?._id)
                        onOpenEnginePosition(rule._id, pair.pairName ?? '');
                    }}
                    sx={{
                      p: 0,
                      ml: 0.4,
                      color: rule?._id ? 'grey.500' : 'grey.700',
                      flexShrink: 0
                    }}
                  >
                    <Inventory2Icon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
              {/* Hedge safety check config — only meaningful once a rule
                  exists. Highlighted when the check is armed. */}
              <Tooltip
                title={rule?._id ? 'Hedge safety check' : 'No rule yet'}
                placement="top"
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={!rule?._id}
                    onClick={e => {
                      e.stopPropagation();
                      if (rule?._id) setHedgeOpen(true);
                    }}
                    sx={{
                      p: 0,
                      ml: 0.4,
                      color: !rule?._id
                        ? 'grey.700'
                        : rule?.hedgeCheckEnabled
                          ? 'warning.main'
                          : 'grey.500',
                      flexShrink: 0
                    }}
                  >
                    <ShieldOutlinedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
              {rule?._id && (
                <HedgeCheckEditorDialog
                  open={hedgeOpen}
                  onClose={() => setHedgeOpen(false)}
                  rule={rule}
                />
              )}
              <Tooltip title="Remove pair from opener page" placement="top">
                <span>
                  <IconButton
                    size="small"
                    disabled={hidePending}
                    onClick={async e => {
                      e.stopPropagation();
                      setHidePending(true);
                      try {
                        await onHidePair(pair);
                      } catch {
                        // The mutation's onError rolls the optimistic cache
                        // back; nothing else to do here.
                      } finally {
                        setHidePending(false);
                      }
                    }}
                    sx={{
                      p: 0,
                      ml: 0.4,
                      color: hidePending ? 'grey.700' : 'grey.500',
                      '&:hover': { color: '#f44336' },
                      flexShrink: 0
                    }}
                  >
                    {hidePending ? (
                      <CircularProgress size={12} />
                    ) : (
                      <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.15,
                  minWidth: 0,
                  flex: 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <a
                    href={
                      pair.polymarket?.marketUrl ??
                      liveState?.polymarketMarketUrl ??
                      undefined
                    }
                    target="_blank"
                    rel="noopener"
                    onClick={e => e.stopPropagation()}
                  >
                    <img
                      src="/assets/exchanges/png/100x100/polymarket.png"
                      alt="P"
                      style={{ width: 13, height: 13, borderRadius: '50%' }}
                    />
                  </a>
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {pair.polymarketTitle || pair.pairName}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <a
                    href={
                      pair.kalshi?.marketUrl ??
                      liveState?.kalshiMarketUrl ??
                      undefined
                    }
                    target="_blank"
                    rel="noopener"
                    onClick={e => e.stopPropagation()}
                  >
                    <img
                      src="/assets/exchanges/png/100x100/kalshi.png"
                      alt="K"
                      style={{ width: 13, height: 13, borderRadius: '50%' }}
                    />
                  </a>
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      color: 'grey.400',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {pair.kalshiTitle || pair.pairName}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </TableCell>

          {/* Position contracts split by side, with Poly on top and
              Kalshi below in each cell. Sourced from current OPEN
              prediction_positions so a flip (e.g. Poly YES closes and
              Poly NO opens) visibly moves contracts between columns. */}
          <TableCell sx={cSx} align="center">
            <Stack spacing={0}>
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  color: contracts.polyYes ? 'grey.200' : 'grey.600'
                }}
              >
                P: {fmtContracts(contracts.polyYes)}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  color: contracts.kalshiYes ? 'grey.300' : 'grey.600'
                }}
              >
                K: {fmtContracts(contracts.kalshiYes)}
              </Typography>
            </Stack>
          </TableCell>
          <TableCell sx={cSx} align="center">
            <Stack spacing={0}>
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  color: contracts.polyNo ? 'grey.200' : 'grey.600'
                }}
              >
                P: {fmtContracts(contracts.polyNo)}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  color: contracts.kalshiNo ? 'grey.300' : 'grey.600'
                }}
              >
                K: {fmtContracts(contracts.kalshiNo)}
              </Typography>
            </Stack>
          </TableCell>

          {/* Eng — engine session-contracts bought per thread (Poly top /
              Kalshi bottom), one column per arb path. Live-only: "—" until
              the engine emits, or for a path the rule isn't running. */}
          {[
            {
              key: 'a' as const,
              eng: sessionForPath(liveState?.believedPosition, 'a')
            },
            {
              key: 'b' as const,
              eng: sessionForPath(liveState?.believedPosition, 'b')
            }
          ].map(({ key, eng }) => (
            <TableCell key={key} sx={cSx} align="center">
              <Tooltip
                title={`Path ${key.toUpperCase()} engine session — Poly ${fmtSession(eng.poly)} · Kalshi ${fmtSession(eng.kalshi)}`}
                placement="top"
              >
                <Stack spacing={0}>
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      color: eng.poly ? 'grey.200' : 'grey.600'
                    }}
                  >
                    P: {fmtSession(eng.poly)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      color: eng.kalshi ? 'grey.300' : 'grey.600'
                    }}
                  >
                    K: {fmtSession(eng.kalshi)}
                  </Typography>
                </Stack>
              </Tooltip>
            </TableCell>
          ))}

          {/* Polymarket — Qty · Bid · Ask · Qty */}
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
              {fmtQty(polyBidSize)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.72rem', color: 'grey.300' }}>
              {fmtPrice(polyBid)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
              {fmtPrice(polyAsk)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
              {fmtQty(polyAskSize)}
            </Typography>
          </TableCell>

          {/* Kalshi — Qty · Bid · Ask · Qty */}
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
              {fmtQty(kalshiBidSize)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.72rem', color: 'grey.300' }}>
              {fmtPrice(kalshiBid)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
              {fmtPrice(kalshiAsk)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
              {fmtQty(kalshiAskSize)}
            </Typography>
          </TableCell>

          {/* Parameters */}
          <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
            <Tooltip
              title={thresholdError ?? ''}
              placement="top"
              disableHoverListener={!thresholdError}
            >
              <span>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0}
                  justifyContent="flex-end"
                >
                  <TextField
                    variant="outlined"
                    size="small"
                    value={threshold}
                    placeholder="0.95"
                    onChange={e => setThreshold(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleCommit();
                    }}
                    error={!!thresholdError}
                    helperText={thresholdError ?? ''}
                    sx={inSx}
                    inputProps={{ step: 0.01, min: 0, max: 0.999 }}
                  />
                  {renderSaveIndicator(
                    saving,
                    threshold !== savedThreshold,
                    handleCommit
                  )}
                </Stack>
              </span>
            </Tooltip>
          </TableCell>
          <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0}
              justifyContent="flex-end"
            >
              <TextField
                variant="outlined"
                size="small"
                value={size}
                placeholder="50"
                onChange={e => setSize(e.target.value)}
                onBlur={handleSave}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleCommit();
                }}
                sx={inSx}
                inputProps={{ step: 1, min: 1 }}
              />
              {renderSaveIndicator(saving, size !== savedSize, handleCommit)}
            </Stack>
          </TableCell>
          <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0}
              justifyContent="flex-end"
            >
              <TextField
                variant="outlined"
                size="small"
                value={maxVol}
                placeholder="500"
                onChange={e => setMaxVol(e.target.value)}
                onBlur={handleSave}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleCommit();
                }}
                sx={inSx}
                inputProps={{ step: 100, min: 0 }}
              />
              {renderSaveIndicator(
                saving,
                maxVol !== savedMaxVol,
                handleCommit
              )}
              {(() => {
                // maxV-hit marker for THIS tab's path (either leg capped → the
                // engine disarmed it and the rule persisted the marker).
                const hit = liveState?.maxVolumeHit?.[path];
                if (!hit || (!hit.kalshi && !hit.polymarket)) return null;
                const legs = [
                  hit.polymarket ? 'Polymarket' : null,
                  hit.kalshi ? 'Kalshi' : null
                ]
                  .filter(Boolean)
                  .join(' + ');
                return (
                  <Tooltip
                    title={`Max volume reached — ${legs} maker leg paused. Raise MaxV or re-arm to resume.`}
                    placement="top"
                  >
                    <WarningAmberIcon
                      sx={{ fontSize: 14, color: '#ff9800', ml: 0.25 }}
                    />
                  </Tooltip>
                );
              })()}
            </Stack>
          </TableCell>

          {/* Per-strategy Sim group — each strategy renders 2 cells:
              [LEGS]: stacked maker (blue) + taker (orange) prices,
                      each tagged with the platform letter (P/K) so
                      the operator never has to guess which platform a
                      number applies to. TT has no maker — both legs
                      are taker (both orange).
              [SUM/TNA]: existing sum + TNA% (unchanged).

              Mirrors the unwinder's per-strategy 2-cell layout
              (UnwindingTabSumOfSides) so reading a strategy is a
              single visual span instead of cross-column assembly. */}
          {(() => {
            // All four notes describe the backend `engineSim` payload —
            // there is no client-estimate path anymore.
            const engineSize = liveState?.engineSim?.sizeUsed;
            const polyTakerNote =
              engineSize != null
                ? `at size ${engineSize} (depth-walked VWAP)`
                : '';
            const kalshiTakerNote =
              engineSize != null
                ? `at size ${engineSize} (depth-walked VWAP)`
                : '';
            const polyMakerNote =
              'engine placement price (floor(threshold − kalshi_taker, 0.001))';
            const kalshiMakerNote =
              'engine placement price (floor(threshold − poly_taker, 0.001))';

            // Style helpers for the maker (blue) + taker (orange) cell
            // contents. The platform letter (P / K) is rendered as a
            // subscript-style chip after the price so the operator can
            // tell at a glance which platform a number applies to.
            const takerSx = {
              fontSize: '0.7rem',
              color: '#ff9800',
              cursor: 'help'
            } as const;
            const makerSx = {
              fontSize: '0.7rem',
              color: '#42a5f5',
              fontWeight: 600,
              cursor: 'help'
            } as const;
            const platformBadgeSx = {
              fontSize: '0.55rem',
              color: 'grey.500',
              fontWeight: 400,
              ml: 0.25
            } as const;

            const polyTakerTooltip =
              polyAsk == null
                ? ''
                : `Polymarket taker — top-of-book ask: ${fmtPrice(polyAsk)}. All-in ${polyTakerNote}: ${fmtPrice(polyAllIn)} (Poly fee ${polyCategoryFeePct}%).`;
            const kalshiTakerTooltip =
              kalshiAsk == null
                ? ''
                : `Kalshi taker — top-of-book ask: ${fmtPrice(kalshiAsk)}. All-in ${kalshiTakerNote}: ${fmtPrice(kalshiAllIn)} (Kalshi fee ${(KALSHI_TAKER_FEE_RATE * 100).toFixed(1)}%).`;
            const polyMakerTooltip = `Polymarket maker rest: ${fmtPrice(polyMakerRest)} — ${polyMakerNote}.`;
            const kalshiMakerTooltip = `Kalshi maker rest: ${fmtPrice(kalshiMakerRest)} — ${kalshiMakerNote}.`;

            // Each strategy: legs cell content + sum + tna. `topLeg` /
            // `bottomLeg` describe the two prices to render, top to
            // bottom in the legs cell. For maker-taker strategies the
            // TAKER (orange) sits on top and the MAKER (blue) below, so
            // every cell reads the same way: the leg we cross first up
            // top, our resting leg beneath it.
            type Leg = {
              price: number | null;
              raw: number | null;
              platform: 'P' | 'K';
              sx: Record<string, unknown>;
              tooltip: string;
            };
            const strategies: Array<{
              key: string;
              tip: string;
              sum: number | null;
              tna: number | null;
              topLeg: Leg;
              bottomLeg: Leg;
            }> = [
              {
                key: 'tt',
                tip: 'Taker-Taker',
                sum: sumTT,
                tna: tnaTT,
                topLeg: {
                  price: polyAllIn,
                  raw: polyAsk,
                  platform: 'P',
                  sx: takerSx,
                  tooltip: polyTakerTooltip
                },
                bottomLeg: {
                  price: kalshiAllIn,
                  raw: kalshiAsk,
                  platform: 'K',
                  sx: takerSx,
                  tooltip: kalshiTakerTooltip
                }
              },
              {
                key: 'polyMk',
                tip: 'Poly maker',
                sum: sumPolyMk,
                tna: tnaPolyMk,
                // Taker (Kalshi) on top, maker (Poly) below.
                topLeg: {
                  price: kalshiAllIn,
                  raw: kalshiAsk,
                  platform: 'K',
                  sx: takerSx,
                  tooltip: kalshiTakerTooltip
                },
                bottomLeg: {
                  price: polyMakerRest,
                  raw: polyMakerRest,
                  platform: 'P',
                  sx: makerSx,
                  tooltip: polyMakerTooltip
                }
              },
              {
                key: 'kalshiMk',
                tip: 'Kalshi maker',
                sum: sumKalshiMk,
                tna: tnaKalshiMk,
                // Taker (Poly) on top, maker (Kalshi) below.
                topLeg: {
                  price: polyAllIn,
                  raw: polyAsk,
                  platform: 'P',
                  sx: takerSx,
                  tooltip: polyTakerTooltip
                },
                bottomLeg: {
                  price: kalshiMakerRest,
                  raw: kalshiMakerRest,
                  platform: 'K',
                  sx: makerSx,
                  tooltip: kalshiMakerTooltip
                }
              }
            ];

            const renderLeg = (leg: Leg) => (
              <Tooltip
                placement="top"
                title={leg.tooltip}
                disableHoverListener={leg.raw == null}
              >
                <Stack
                  direction="row"
                  alignItems="baseline"
                  spacing={0}
                  justifyContent="flex-end"
                >
                  <Typography sx={leg.sx}>{fmtPrice(leg.price)}</Typography>
                  <Typography component="span" sx={platformBadgeSx}>
                    {leg.platform}
                  </Typography>
                </Stack>
              </Tooltip>
            );

            return (
              <>
                {strategies.map(({ key, tip, sum, tna, topLeg, bottomLeg }) => (
                  <Fragment key={key}>
                    {/* Legs */}
                    <TableCell sx={cSx} align="right">
                      <Stack spacing={0} alignItems="flex-end">
                        {renderLeg(topLeg)}
                        {renderLeg(bottomLeg)}
                      </Stack>
                    </TableCell>
                    {/* Sum + TNA */}
                    <TableCell sx={cSx} align="right">
                      <Tooltip title={tip} placement="top">
                        <Stack spacing={0} alignItems="flex-end">
                          <Typography
                            sx={{
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              color: sumColor(sum)
                            }}
                          >
                            {fmtPrice(sum)}
                          </Typography>
                          <Typography
                            sx={{ fontSize: '0.62rem', color: tnaColor(tna) }}
                          >
                            {fmtPct(tna)}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    </TableCell>
                  </Fragment>
                ))}
              </>
            );
          })()}

          {/* Ctrl — Poly maker · Kalshi maker · TT */}
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            {togglingFlag === polyFlag ? (
              <CircularProgress size={14} />
            ) : (
              <Tooltip
                title={`Poly ${path === 'a' ? 'YES' : 'NO'} as maker`}
                placement="top"
              >
                <span>
                  <Switch
                    size="small"
                    checked={polyMakerOn}
                    onChange={(_, v) => void doSetMakerFlag(polyFlag, v)}
                  />
                </span>
              </Tooltip>
            )}
          </TableCell>
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            {togglingFlag === kalshiFlag ? (
              <CircularProgress size={14} />
            ) : (
              <Tooltip
                title={`Kalshi ${path === 'a' ? 'NO' : 'YES'} as maker`}
                placement="top"
              >
                <span>
                  <Switch
                    size="small"
                    checked={kalshiMakerOn}
                    onChange={(_, v) => void doSetMakerFlag(kalshiFlag, v)}
                  />
                </span>
              </Tooltip>
            )}
          </TableCell>
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            {anyRule && (anyRule.balancingContractsRemaining ?? 0) > 0 ? (
              <Tooltip
                title={`Balancing: ${anyRule.balancingContractsRemaining} contracts resting on ${anyRule.balancingLeg}`}
                placement="top"
              >
                <CircularProgress size={14} />
              </Tooltip>
            ) : togglingTT ? (
              <CircularProgress size={14} />
            ) : (
              <Tooltip
                title={
                  ttOn
                    ? 'Taker-Taker on. Click to switch off.'
                    : 'Taker-Taker: fire BOTH legs as parallel takers when combined cost clears threshold.'
                }
                placement="top"
              >
                <span>
                  <Switch
                    size="small"
                    checked={ttOn}
                    onChange={(_, v) => void handleTTToggle(v)}
                  />
                </span>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>

        {/* Expanded per-rule TNA simulation breakdown — renders only what
            the Rust engine emits on `liveState.simulation`. */}
        {isExpanded && (
          <TableRow data-expansion-index={dataIndex}>
            <TableCell
              colSpan={COL_WIDTHS_PX.length}
              sx={{
                py: 0,
                px: 1,
                borderBottom: '1px solid rgba(255,255,255,0.06)'
              }}
            >
              <EntrySimulationBreakdownPanel
                polyAsMaker={liveState?.simulation?.polyAsMaker ?? null}
                kalshiAsMaker={liveState?.simulation?.kalshiAsMaker ?? null}
                expiryDate={liveState?.expiryDate ?? pair.expiryDate}
                daysToExpiry={liveState?.daysToExpiry}
              />
            </TableCell>
          </TableRow>
        )}
      </>
    );
  },
  (prev, next) =>
    // `dataIndex` must be compared so the row re-renders (and re-stamps its
    // `data-index`) when a sort reorders it — react-virtual keys measured
    // heights off that attribute. `measureRef` is a stable callback.
    prev.dataIndex === next.dataIndex &&
    prev.path === next.path &&
    prev.pair === next.pair &&
    prev.rule === next.rule &&
    prev.anyRule === next.anyRule &&
    // `liveState` is no longer a prop — the row reads it reactively via
    // `useLiveState(store)`, so a price tick re-renders ONLY the affected
    // row without flowing through this memo. `store` is a stable instance.
    prev.store === next.store &&
    prev.pairKey === next.pairKey &&
    prev.isExpanded === next.isExpanded &&
    prev.onToggleExpand === next.onToggleExpand &&
    prev.selected === next.selected &&
    prev.onToggleSelect === next.onToggleSelect &&
    prev.onCreateRule === next.onCreateRule &&
    prev.onSetMakerFlag === next.onSetMakerFlag &&
    prev.onUpdateRule === next.onUpdateRule &&
    prev.onHidePair === next.onHidePair &&
    prev.onOpenEnginePosition === next.onOpenEnginePosition
);
